/** D-ID Agent ID for real-time avatar (from D-ID Studio or List Agents API) */

/** FFT size for Web Audio analyser (frequency bin count = fftSize / 2) */
export const ANALYSER_FFT_SIZE = 512;

/** Buffer size for ScriptProcessorNode (samples per processing block) */
export const SCRIPT_PROCESSOR_BUFFER_SIZE = 4096;

// ======================
// CHROMA KEY (green screen)
// ======================

/** Minimum green channel value to consider pixel as green */
export const CHROMA_MIN_GREEN = 80;

/** Green must be this many times greater than R and B to count as green */
export const CHROMA_DOMINANCE = 1.35;

/** Softness factor for alpha blend at green-screen edges */
export const CHROMA_SOFTNESS = 40;

// ======================
// SHIRT RECOLOR (optional, runs in the chroma-key pass)
// ======================

/** Off: the avatar's own blue shirt suits the blue theme. Flip to true to
 *  recolour it again (SHIRT_RECOLOR_RGB below). */
export const SHIRT_RECOLOR_ENABLED = false;

/** Colour the shirt takes at its original mid-tone; folds and highlights scale from it */
export const SHIRT_RECOLOR_RGB = [228, 206, 176];

/** Median luma of the original blue shirt — pixels this bright get SHIRT_RECOLOR_RGB exactly */
export const SHIRT_REFERENCE_LUMA = 88;

/** Share of the colour kept in the deepest folds (0 = shadows go fully black) */
export const SHIRT_SHADE_FLOOR = 0.15;

/** Blueness, (b - r) / b, where recolouring starts, and the span over which it reaches full strength */
export const SHIRT_BLUE_START = 0.05;
export const SHIRT_BLUE_RAMP = 0.2;

// ======================
// SILENCE DETECTION
// ======================

/** Below this = silence/noise (start 3s countdown). Above SPEECH_* = user talking (cancel countdown). */
export const SILENCE_THRESHOLD_START_MIC = 15;
export const SILENCE_THRESHOLD_TOGGLE_MIC = 15;

/** Above this = user is talking (resets silence timer). Keeps ambient noise from cancelling. */
export const SPEECH_THRESHOLD_START_MIC = 28;
export const SPEECH_THRESHOLD_TOGGLE_MIC = 28;

/** How long silence/noise must last (ms) after user stopped talking before auto-stopping mic */
export const SILENCE_DURATION_MS = 700;

// ======================
// AVATAR PLAYBACK
// ======================

/**
 * Extra time (ms) to keep the WebRTC video playing after the server sends
 * stream/done. The signal arrives before the browser finishes buffered audio.
 */
export const STREAM_DONE_PLAYBACK_TAIL_MS = 900;

// ======================
// WEBRTC
// ======================

/**
 * STUN servers used only when the backend's avatar_realtime answer carries no
 * usable `ice_servers` (RealTimeAvatarAPI validates the list first). Google's
 * public STUN. A TURN server, if strict networks ever need one, belongs in the
 * backend's answer, not here.
 */
export const FALLBACK_ICE_SERVERS = [
  { urls: "stun:stun.l.google.com:19302" },
  { urls: "stun:stun1.l.google.com:19302" },
];

// ======================
// EXPORTS
// ======================

const realTimeAvatarConfig = {
  ANALYSER_FFT_SIZE,
  SCRIPT_PROCESSOR_BUFFER_SIZE,
  CHROMA_MIN_GREEN,
  CHROMA_DOMINANCE,
  CHROMA_SOFTNESS,
  SHIRT_RECOLOR_ENABLED,
  SHIRT_RECOLOR_RGB,
  SHIRT_REFERENCE_LUMA,
  SHIRT_SHADE_FLOOR,
  SHIRT_BLUE_START,
  SHIRT_BLUE_RAMP,
  SILENCE_THRESHOLD_START_MIC,
  SILENCE_THRESHOLD_TOGGLE_MIC,
  SPEECH_THRESHOLD_START_MIC,
  SPEECH_THRESHOLD_TOGGLE_MIC,
  SILENCE_DURATION_MS,
  STREAM_DONE_PLAYBACK_TAIL_MS,
  FALLBACK_ICE_SERVERS,
};

export default realTimeAvatarConfig;
